<script lang="ts">
  /**
   * The scene: one collapsible section per layer (visualization, its controls, and its own or
   * shared colours and effects), then the overall look shared by every layer, motion,
   * transitions, modulations and Auto show.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { LFO_SHAPES, sourceLabel, type LfoShape } from '../../shared/modulation';
  import {
    BLEND_LABELS,
    BLEND_MODES,
    MAX_LAYERS,
    QUANTIZE_OPTIONS,
    TRANSITION_TYPES,
    resolveTarget,
    type BlendMode,
    type Quantize,
    type TransitionType,
  } from '../../shared/scene';
  import AccordionSection from '../components/AccordionSection.svelte';
  import AutoShow from '../components/AutoShow.svelte';
  import EffectsEditor from '../components/EffectsEditor.svelte';
  import LayerPanel from '../components/LayerPanel.svelte';
  import OverallColours from '../components/OverallColours.svelte';
  import ParamControl from '../components/ParamControl.svelte';
  import Toggle from '../components/Toggle.svelte';
  import VizPicker from '../components/VizPicker.svelte';
  import Icon from '../components/Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  const scene = $derived(ctl.scene);
  let adding = $state(false);
  /** The selected layer is the open one; clicking its header again folds it. */
  let folded = $state(false);
  /** Which of the shared sections below the layers is open. */
  let section = $state<string | null>(null);
  const toggleSection = (id: string) => (section = section === id ? null : id);

  function openLayer(id: string) {
    if (ctl.selectedLayerId === id) folded = !folded;
    else {
      ctl.selectedLayerId = id;
      folded = false;
    }
  }

  const presetSettings = [
    { key: 'intensity', label: 'Intensity', min: 0, max: 2 },
    { key: 'beatReact', label: 'Beat reaction', min: 0, max: 2 },
    { key: 'smoothing', label: 'Smoothing', min: 0, max: 1 },
  ] as const;
</script>

<section class="sec" data-tour="quick">
  <div class="row quick">
    <button
      class="primary grow"
      data-testid="surprise"
      title={`A random new look: visualizations, palette and effects${ctl.keyHint('scene.surprise')}. Undo goes back.`}
      onclick={() => ctl.surprise()}><Icon name="dice" /> Surprise me</button
    >
    <button
      class="fav"
      class:on={ctl.currentIsFavorite}
      aria-pressed={ctl.currentIsFavorite}
      data-testid="favorite-toggle"
      title={`Save to favorites${ctl.keyHint('favorite.add')}`}
      onclick={() => ctl.toggleFavorite()}
      ><Icon name={ctl.currentIsFavorite ? 'heart-filled' : 'heart'} /> Favorite</button
    >
  </div>
</section>

<section class="sec layers-sec" data-tour="layers">
  <div class="sec-head">
    <h2>Layers</h2>
    <span class="chip">{scene.layers.length}/{MAX_LAYERS}</span>
    {#if scene.layers.length < MAX_LAYERS}
      <button class="link add" data-testid="add-layer" onclick={() => (adding = !adding)}
        >{adding ? 'Cancel' : '+ Add layer'}</button
      >
    {/if}
  </div>
  {#if adding}
    <div class="adding" data-testid="add-layer-picker">
      <VizPicker
        {ctl}
        current=""
        onpick={(id) => {
          ctl.addLayer(id);
          adding = false;
          folded = false;
        }}
      />
    </div>
  {/if}
  <div class="layers" data-testid="layers">
    {#each [...scene.layers].reverse() as l (l.id)}
      {@const i = scene.layers.indexOf(l)}
      {@const open = l.id === ctl.selectedLayer.id && !folded}
      <div class="layer" class:open class:selected={l.id === ctl.selectedLayer.id}>
        <div class="layer-head">
          <input
            type="checkbox"
            checked={l.enabled}
            aria-label="Layer enabled"
            onchange={(e) =>
              ctl.dispatch({
                type: 'layer.set',
                layerId: l.id,
                patch: { enabled: e.currentTarget.checked },
              })}
          />
          <button
            class="name grow"
            aria-expanded={open}
            onclick={() => openLayer(l.id)}
            data-testid={`layer-${i}`}
          >
            <span class="chev"><Icon name={open ? 'chevron-down' : 'chevron-right'} /></span>
            {ctl.vizDef(l.vizId)?.name ?? l.vizId}
            {#if l.palette || l.postfx}<span
                class="chip accent"
                title="Has its own colours or effects">own look</span
              >{/if}
          </button>
          <button
            class="icon-btn pin"
            class:on={ctl.isLayerPinned(l.id)}
            aria-label="Pin layer (keep it when randomizing)"
            aria-pressed={ctl.isLayerPinned(l.id)}
            title={ctl.isLayerPinned(l.id)
              ? 'Pinned: Surprise me and Auto show keep this layer'
              : 'Pin: keep this layer when randomizing'}
            data-testid={`layer-pin-${i}`}
            onclick={() => ctl.togglePin({ layerId: l.id })}
            ><Icon name={ctl.isLayerPinned(l.id) ? 'pin-filled' : 'pin'} /></button
          >
          {#if i > 0}
            <select
              value={l.blend}
              aria-label="Blend mode"
              onchange={(e) =>
                ctl.dispatch({
                  type: 'layer.set',
                  layerId: l.id,
                  patch: { blend: e.currentTarget.value as BlendMode },
                })}
            >
              {#each BLEND_MODES as b (b)}
                <option value={b}>{BLEND_LABELS[b]}</option>
              {/each}
            </select>
          {/if}
          <button
            class="icon-btn"
            aria-label="Move up"
            disabled={i === scene.layers.length - 1}
            onclick={() => ctl.dispatch({ type: 'layer.move', layerId: l.id, delta: 1 })}
            ><Icon name="arrow-up" /></button
          >
          <button
            class="icon-btn"
            aria-label="Move down"
            disabled={i === 0}
            onclick={() => ctl.dispatch({ type: 'layer.move', layerId: l.id, delta: -1 })}
            ><Icon name="arrow-down" /></button
          >
          <button
            class="icon-btn"
            aria-label="Remove layer"
            disabled={scene.layers.length <= 1}
            onclick={() => ctl.dispatch({ type: 'layer.remove', layerId: l.id })}
            ><Icon name="close" /></button
          >
        </div>
        {#if open}
          <div class="layer-body" data-testid={`layer-body-${i}`}>
            <LayerPanel {ctl} layer={l} index={i} />
          </div>
        {/if}
      </div>
    {/each}
  </div>
</section>

<div>
  <div data-tour="auto">
    <AccordionSection
      id="auto"
      title="Auto"
      meta={ctl.settings.autopilot
        ? ctl.autoNextBars !== null && !ctl.previewMode
          ? `on · next in ${ctl.autoNextBars} bar${ctl.autoNextBars === 1 ? '' : 's'}`
          : 'on'
        : 'off'}
      open={section === 'auto'}
      ontoggle={() => toggleSection('auto')}
    >
      <AutoShow {ctl} />
    </AccordionSection>
  </div>

  <AccordionSection
    id="overall-colours"
    title="Overall colours"
    meta={ctl.pins.palette ? 'pinned' : 'all layers'}
    open={section === 'colours'}
    ontoggle={() => toggleSection('colours')}
  >
    <Toggle
      label="Pin colours (keep them when randomizing)"
      checked={ctl.pins.palette}
      testid="pin-palette"
      onchange={() => ctl.togglePin('palette')}
    />
    <OverallColours {ctl} />
  </AccordionSection>

  <div data-tour="effects">
    <AccordionSection
      id="overall-effects"
      title="Overall effects"
      meta={ctl.pins.effects ? 'pinned' : 'the whole picture'}
      open={section === 'effects'}
      ontoggle={() => toggleSection('effects')}
    >
      <p class="hint first">
        These apply to the finished picture. To use one on a single layer, open the layer and choose
        “This layer only”.
      </p>
      <Toggle
        label="Pin effects (keep them when randomizing)"
        checked={ctl.pins.effects}
        testid="pin-effects"
        onchange={() => ctl.togglePin('effects')}
      />
      <EffectsEditor {ctl} />
    </AccordionSection>
  </div>

  <AccordionSection
    id="motion"
    title="Motion & transition"
    open={section === 'motion'}
    ontoggle={() => toggleSection('motion')}
  >
    <h3 class="sub-title">Motion <span class="chip">saved in preset</span></h3>
    {#each presetSettings as s (s.key)}
      <ParamControl
        {ctl}
        def={{ id: s.key, label: s.label, type: 'float', min: s.min, max: s.max, default: 1 }}
        value={ctl.settings[s.key]}
        target={s.key === 'intensity' ? 'master/intensity' : `setting/${s.key}`}
        modulatable={s.key === 'intensity'}
        onchange={(v) => ctl.setSetting(s.key, v)}
      />
    {/each}
    <div class="sub-block">
      <h3>Transition into this scene</h3>
      <label class="field">
        <span>Style</span>
        <select
          value={scene.transition.type}
          onchange={(e) =>
            ctl.dispatch({
              type: 'transition.set',
              patch: { type: e.currentTarget.value as TransitionType },
            })}
        >
          {#each TRANSITION_TYPES as t (t)}<option value={t}>{t}</option>{/each}
        </select>
      </label>
      <label class="field">
        <span>Length</span>
        <div class="row">
          <input
            type="number"
            min="0"
            step={scene.transition.unit === 'beats' ? 0.5 : 50}
            value={scene.transition.duration}
            onchange={(e) =>
              ctl.dispatch({
                type: 'transition.set',
                patch: { duration: Number(e.currentTarget.value) },
              })}
          />
          <select
            value={scene.transition.unit}
            onchange={(e) =>
              ctl.dispatch({
                type: 'transition.set',
                patch: { unit: e.currentTarget.value as 'ms' | 'beats' },
              })}
          >
            <option value="beats">beats</option>
            <option value="ms">ms</option>
          </select>
        </div>
      </label>
      <label class="field">
        <span>Start on</span>
        <select
          value={scene.transition.quantize}
          onchange={(e) =>
            ctl.dispatch({
              type: 'transition.set',
              patch: { quantize: e.currentTarget.value as Quantize },
            })}
        >
          {#each QUANTIZE_OPTIONS as q (q)}
            <option value={q}
              >{q === 'immediate'
                ? 'immediately'
                : q === 'drop'
                  ? 'next drop (files)'
                  : `next ${q}`}</option
            >
          {/each}
        </select>
      </label>
      <p class="hint">Used when this scene is recalled from a preset slot or by Auto show.</p>
    </div>
  </AccordionSection>

  <AccordionSection
    id="modulations"
    title="Modulations & LFOs"
    meta={scene.modulations.length ? String(scene.modulations.length) : ''}
    open={section === 'mods'}
    ontoggle={() => toggleSection('mods')}
  >
    {#if scene.modulations.length === 0}
      <p class="hint">
        None yet. Use <Icon name="wave" /> next to any parameter to make it oscillate, or <Icon
          name="modulate"
        /> to drive it with the music.
      </p>
    {:else}
      <div class="list">
        {#each scene.modulations as m (m.id)}
          {@const info = resolveTarget(scene, m.target, (id) => ctl.vizDef(id))}
          <div class="item small">
            <span class="grow">{sourceLabel(m.source)} → {info?.label ?? m.target}</span>
            <span class="chip">{m.amount > 0 ? '+' : ''}{m.amount.toFixed(2)}</span>
            <button
              class="icon-btn"
              aria-label="Remove"
              onclick={() => ctl.dispatch({ type: 'mod.remove', id: m.id })}
              ><Icon name="close" /></button
            >
          </div>
        {/each}
      </div>
    {/if}
    <details class="sub">
      <summary>LFOs</summary>
      {#each scene.lfos as lfo, i (lfo.id)}
        <div class="row lfo">
          <span class="muted small">LFO {i + 1}</span>
          <select
            aria-label={`LFO ${i + 1} shape`}
            value={lfo.shape}
            onchange={(e) =>
              ctl.dispatch({
                type: 'lfo.update',
                id: lfo.id,
                patch: { shape: e.currentTarget.value as LfoShape },
              })}
          >
            {#each LFO_SHAPES as s (s)}<option value={s}>{s}</option>{/each}
          </select>
          <input
            type="number"
            min="0.01"
            max="64"
            step="0.25"
            value={lfo.rate}
            aria-label="Rate"
            onchange={(e) =>
              ctl.dispatch({
                type: 'lfo.update',
                id: lfo.id,
                patch: { rate: Number(e.currentTarget.value) },
              })}
          />
          <select
            aria-label={`LFO ${i + 1} rate unit`}
            value={lfo.sync ? 'beats' : 'hz'}
            onchange={(e) =>
              ctl.dispatch({
                type: 'lfo.update',
                id: lfo.id,
                patch: { sync: e.currentTarget.value === 'beats' },
              })}
          >
            <option value="beats">beats/cycle</option>
            <option value="hz">Hz</option>
          </select>
        </div>
      {/each}
    </details>
  </AccordionSection>
</div>

<style>
  .quick {
    gap: 8px;
  }
  .fav.on {
    border-color: var(--accent-2);
    color: var(--accent-2);
  }
  .add {
    margin-left: auto;
  }
  .adding {
    margin-bottom: 10px;
    padding: 8px;
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
  }
  .layers {
    display: grid;
    gap: 6px;
  }
  .layer {
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }
  .layer.selected {
    border-color: color-mix(in srgb, var(--accent) 50%, var(--border));
  }
  .layer-head {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 6px;
  }
  .layer-head .name {
    border: none;
    background: none;
    text-align: left;
    padding: 4px 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    gap: 6px;
    align-items: center;
  }
  .chev {
    display: inline-flex;
    color: var(--muted);
  }
  .layer-head select {
    font-size: 11.5px;
    padding: 2px 4px;
    max-width: 110px;
  }
  .layer-body {
    padding: 0 10px 8px;
    border-top: 1px solid var(--border);
  }
  .sub-title,
  :global(.sub-block h3) {
    margin: 10px 0 6px;
    font-size: 11.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
  }
  .lfo {
    margin: 4px 0;
  }
  .lfo input {
    width: 64px;
  }
  .lfo select {
    font-size: 12px;
  }
  input[type='number'] {
    width: 80px;
  }
</style>
